import { getDsProcess, getUpdate } from "@/components/ds/lib/dsApi";
import { PlaylistItemDTO } from "@/components/ds/lib/types";
import { DSPlayer } from "@/components/ds/player/DSPlayer";
import { useState, useEffect, useRef, useCallback } from "react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import settings from "@/settings.json";
import { useSearchParams } from "react-router";
import { computePlaylistFromProcessData } from "@/components/ds/lib/DSProcessor";
// import {
//   savePlaylist,
//   loadPlaylist,
//   isOnline,
//   registerConnectivityListeners,
//   clearOfflineData,
// } from "@/components/ds/lib/DSDataLayer";
import { CheckIcon } from "lucide-react";
import { DSPageRegisterFlow } from "./DSPageRegisterFlow";
import { ROUTES } from "@/constants/routeConstants";
import { requestFullScreenWithUserPrompt } from "./DSScreenUtils";
import { DSOnboardingPage } from "./DSOnboardingPage";

// Fixed interval for updateCheck
const ContentSourceUpdateCheckSeconds_MS = Number(settings.ContentSourceUpdateCheckSeconds) * 1000;

export const DSPage = () => {
  const [playlist, setPlaylist] = useState<PlaylistItemDTO[]>([]);
  const [registeredCode, setRegisteredCode] = useState(localStorage.getItem("Code") || null);
  const [licenceExpired, setLicenceExpired] = useState(localStorage.getItem("LicenceExpired") === "true");
  const [queryParams] = useSearchParams();
  // const [isOfflineMode, setIsOfflineMode] = useState(!isOnline());
  const debug = queryParams.get("debug") === "true";

  const isRegistered = !!registeredCode;
  const isEmptyPlaylist = playlist.length === 0;
  const isLicenceExpired = licenceExpired;
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const processIntervalRef = useRef<NodeJS.Timeout>();
  const lastLoadTimeRef = useRef<number>(0);
  // Move the hasRun ref to the component top level
  const initialLoadComplete = useRef<boolean>(false);

  const [onboarding, setOnboarding] = useState(true);

  useEffect(() => {
    requestFullScreenWithUserPrompt();
  }, []);

  // Load data with offline support
  const loadData = useCallback(async () => {
    // Throttle the loadData calls
    const now = Date.now();
    const timeSinceLastLoad = now - lastLoadTimeRef.current;
    if (timeSinceLastLoad < ContentSourceUpdateCheckSeconds_MS && lastLoadTimeRef.current !== 0) {
      console.log(`Skipping loadData - called too soon (${timeSinceLastLoad}ms since last call)`);
      return;
    }

    console.log("loadData called at:", new Date().toISOString());
    lastLoadTimeRef.current = now;

    try {
      // Try to load from network if online
      if (registeredCode) {
        const uniqueId = localStorage.getItem("UniqueId") || "";
        const processData = await getDsProcess(uniqueId, registeredCode);
        if (processData) {
          const computedPlaylist = computePlaylistFromProcessData(processData);
          console.log("[DS]computedPlaylist", computedPlaylist);
          setPlaylist(computedPlaylist);

          const totalDuration = computedPlaylist.reduce((acc, item) => acc + item.duration, 0);
          console.log("[DS] TotalDuration", totalDuration);
          setTimeout(() => {
            setPlaylist(computePlaylistFromProcessData(processData));
          }, 1000 * totalDuration);
        }
      }
      // // Use cached data if offline or network request fails
      // else {
      //   const cachedPlaylist = await loadPlaylist();
      //   if (cachedPlaylist && cachedPlaylist.length > 0) {
      //     setPlaylist(cachedPlaylist);

      //     // If we're offline, explicitly set offline mode
      //     if (!isOnline()) {
      //       setIsOfflineMode(true);
      //     }
      //   }
      // }
    } catch (error) {
      console.error("Error loading data:", error);

      // Try to load from cache as fallback on error
      // const cachedPlaylist = await loadPlaylist();
      // if (cachedPlaylist && cachedPlaylist.length > 0) {
      //   setPlaylist(cachedPlaylist);
      // }
    }
  }, [registeredCode, setPlaylist]);

  // Define updateCheck inside the component
  const updateCheck = useCallback(async () => {
    const uniqueId = localStorage.getItem("UniqueId");
    if (!uniqueId) {
      console.log("updateCheck UniqueId not found");
      return;
    }

    const code = localStorage.getItem("Code");
    if (!code) {
      console.log("updateCheck Code not found");
      return;
    }

    try {
      const data = await getUpdate(uniqueId, code);

      switch (data) {
        case "No_Licence":
          localStorage.setItem("LicenceExpired", "false");
          setLicenceExpired(false);
          location.reload();
          break;
        case "Licence_Expired":
          localStorage.setItem("LicenceExpired", "true");
          setLicenceExpired(true);
          location.reload();
          break;
        case "No_Player":
          localStorage.clear();
          setRegisteredCode(null);
          setPlaylist([]);
          break;
        case "Update_Required":
          location.reload();
          break;
        case "Restart_Required":
          localStorage.removeItem("LicenceExpired");
          localStorage.removeItem("UniqueId");
          localStorage.removeItem("Code");
          location.href = ROUTES.DS;
          // await clearOfflineData();
          break;
        case "No_Action_Required":
          localStorage.removeItem("LicenceExpired");
          setLicenceExpired(false);
          break;
        default:
          break;
      }
    } catch (err) {
      console.error(err);
    }
  }, [loadData, setLicenceExpired, setRegisteredCode, setPlaylist]);

  useEffect(() => {
    console.log(
      `Setting up interval at ${new Date().toISOString()} - ContentSourceUpdateCheckSeconds_MS: ${ContentSourceUpdateCheckSeconds_MS}ms`
    );

    // Only set up the interval if it hasn't been set already
    if (!intervalRef.current) {
      intervalRef.current = setInterval(() => {
        console.log(`Interval triggered at ${new Date().toISOString()} - running updateCheck`);
        updateCheck();
      }, ContentSourceUpdateCheckSeconds_MS);

      console.log(`Interval successfully created with ID: ${intervalRef.current}`);
    }

    // Return cleanup function
    return () => {
      if (intervalRef.current) {
        console.log(`Clearing interval with ID: ${intervalRef.current}`);
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []); // Empty dependency array so it only runs once when component mounts

  // One-time only initial data loading
  useEffect(() => {
    if (initialLoadComplete.current) {
      return; // Skip if already run once
    }

    if (!licenceExpired && !initialLoadComplete.current) {
      console.log("ONE-TIME Initial loadData call at:", new Date().toISOString());
      loadData();
      initialLoadComplete.current = true;
    }
  }, [licenceExpired, loadData]);

  // Setup connectivity listeners and cleanup
  useEffect(() => {
    // Setup connectivity listeners
    // const cleanup = registerConnectivityListeners(handleOnline, handleOffline);

    // Initial offline check
    // setIsOfflineMode(!isOnline());

    // Try to load data regardless of online status - if we're offline, it will use cached data
    if (!initialLoadComplete.current && !licenceExpired) {
      console.log("Attempting to load data on mount (may use cache if offline)");
      loadData();
      initialLoadComplete.current = true;
    }

    return () => {
      // Remove the intervalRef.current cleanup from here to avoid duplicate cleanup
      if (processIntervalRef.current) {
        clearInterval(processIntervalRef.current);
      }
      // cleanup();
    };
  }, [loadData, licenceExpired]);

  // Add a special case for being offline with no registration yet
  const showRegistrationForm = !isRegistered && !isLicenceExpired && !onboarding;
  const showOnboarding = onboarding && !isRegistered && !isLicenceExpired;

  return (
    <div className="w-full min-h-full flex flex-col gap-4">
      {/* Always show OfflineIndicator when offline */}
      {/* {isOfflineMode && <OfflineIndicator />} */}

      {isLicenceExpired && (
        <Alert variant="destructive">
          <AlertTitle>Licence Expired</AlertTitle>
          <AlertDescription>Your licence has expired. Please register a new code to continue.</AlertDescription>
        </Alert>
      )}

      {showOnboarding && <DSOnboardingPage onDismiss={() => setOnboarding(false)} />}

      {/* Show registration form only if we're online and not registered */}
      {showRegistrationForm && (
        <DSPageRegisterFlow
          onRegistered={(code) => {
            localStorage.setItem("Code", code);
            setRegisteredCode(code);
            location.reload();
          }}
        />
      )}

      {/* Show content if registered or if we have offline content available */}
      {isRegistered && (
        <>
          {isEmptyPlaylist && !isLicenceExpired && (
            <div className="container mx-auto p-4 max-w-2xl">
              <Alert className="mt-6">
                <AlertTitle className="flex items-center gap-2 text-lg">
                  Player Active <CheckIcon className="w-4 h-4 text-green-500" />
                </AlertTitle>
                <AlertDescription className="text-lg">
                  There is no content to display. Please assign content to this player.
                </AlertDescription>
              </Alert>
            </div>
          )}

          {!isEmptyPlaylist && <DSPlayer list={playlist} debug={debug} isFullScreen={true} />}
        </>
      )}
    </div>
  );
};
